import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, FormControlLabel, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { request, useOperations } from './OperationsProvider';
import { humanMs } from './areas';
import DownloadTable from '../shell/DownloadTable';
import { formatDateTime } from '../common/util/datetime';
import {
  corridorsOf, runsOn, summarise, heatmap, hoursWithRuns, daysWithRuns, narrow, vehiclesOn,
  byVehicle, driverOf, DAYS,
} from './corridors';

// How long the run between two places takes, and when it does not.
//
// A plant's own stages are one question; this is the other one - the leg between two warehouses, or
// out to a customer and back. The product already knows when a lorry entered and left every
// geofenced area, so a run is a pair of those, and once runs exist the questions people actually ask
// become answerable: what does this normally take, which lorries are slow on it, and at what hour of
// which day does it fall apart.
//
// Nothing here is worked out from raw crossings. The server pairs them on the scheduler; the page
// filters and summarises a few hundred rows, which is browser work.

const day = (offset) => new Date(Date.now() - offset * 86400000).toISOString().slice(0, 10);

const BetweenAreasPage = () => {
  const navigate = useNavigate();
  const { data, trips } = useOperations();
  const [journeys, setJourneys] = useState(null);
  const [pairedAt, setPairedAt] = useState(null);
  const [error, setError] = useState(null);
  const [corridor, setCorridor] = useState('');
  const [from, setFrom] = useState(day(29));
  const [to, setTo] = useState(day(0));
  const [vehicleId, setVehicleId] = useState('');
  const [lateOnly, setLateOnly] = useState(false);
  // Set by clicking a square on the heatmap, cleared by clicking it again.
  const [when, setWhen] = useState(null);

  // Asked again while the page is open. The scheduler re-pairs about every quarter of an hour, and
  // this screen is one somebody leaves up - so a single fetch on mount meant a control room watching
  // whatever the pairing happened to be when the tab was opened.
  useEffect(() => {
    let live = true;
    const load = () => request('/api/operations/area-journeys')
      .then((held) => {
        if (!live) return;
        setJourneys(held?.journeys || []);
        setPairedAt(held?.pairedAt || null);
        setError(null);
      })
      // A failed refresh leaves the runs already on screen, which is better than emptying a table
      // somebody is reading. The notice says that what is shown is the last good pairing.
      .catch((err) => { if (live) setError(err.message); });
    load();
    const timer = setInterval(load, 120000);
    return () => { live = false; clearInterval(timer); };
  }, []);

  const corridors = useMemo(() => corridorsOf(journeys || []), [journeys]);
  const chosen = corridor || corridors[0]?.key || '';
  // Everything on this leg in these dates. The heatmap is built from this rather than from the
  // narrowed set, so choosing one lorry does not redraw the chart that is being used to choose.
  const onLeg = useMemo(
    () => runsOn(journeys || [], chosen, from, `${to}T23:59:59`),
    [journeys, chosen, from, to],
  );
  const runs = useMemo(
    () => narrow(onLeg, { vehicleId, lateOnly, day: when?.day ?? null, hour: when?.hour ?? null }),
    [onLeg, vehicleId, lateOnly, when],
  );
  const lorries = useMemo(() => vehiclesOn(onLeg, data.vehicles), [onLeg, data.vehicles]);
  // Per lorry, over everything on the leg rather than the narrowed set: the point of the table is to
  // choose which lorry to look at, so it must not change when one is chosen.
  const perVehicle = useMemo(() => byVehicle(onLeg, data.vehicles, trips), [onLeg, data.vehicles, trips]);
  const head = useMemo(() => summarise(runs), [runs]);
  const cells = useMemo(() => heatmap(onLeg), [onLeg]);
  const hours = useMemo(() => hoursWithRuns(cells), [cells]);
  const days = useMemo(() => daysWithRuns(cells), [cells]);
  const worstCell = useMemo(
    () => cells.filter((c) => c.runs >= 2).reduce((held, c) => (!held || c.ms > held.ms ? c : held), null),
    [cells],
  );
  const slowest = head ? head.slowest : 0;

  // Choosing a different leg clears the choices that were about the old one. Keeping them is how a
  // page comes to show "no runs" for a leg that has plenty.
  useEffect(() => { setVehicleId(''); setWhen(null); }, [chosen]);

  const columns = [
    ['Vehicle', (r) => data.vehicles?.find((v) => v.id === r.vehicleId)?.name || `Vehicle ${r.vehicleId}`],
    ['Driver', (r) => driverOf(r, trips)],
    ['From', (r) => r.from],
    ['To', (r) => r.to],
    ['Left', (r) => formatDateTime(r.leftAt)],
    ['Arrived', (r) => formatDateTime(r.arrivedAt)],
    ['Took (min)', (r) => Math.round(r.ms / 60000)],
    ['Usual (min)', (r) => (r.normalMs == null ? '' : Math.round(r.normalMs / 60000))],
    ['Over (min)', (r) => (r.overMs == null ? '' : Math.round(r.overMs / 60000))],
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700}>Area journeys</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        How long the trip from one geofenced area to another takes, and when it does not.
      </Typography>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {journeys === null
            ? `Trips are unavailable: ${error}`
            : `Showing the last pairing that loaded; the latest could not be fetched (${error}).`}
        </Alert>
      )}
      {journeys === null && !error && <Typography color="text.secondary">Loading trips…</Typography>}
      {/* How old the figures are. The pairing runs on the scheduler, so the answer to "is this
          today's run" is a property of the set and not of the moment the page was opened. */}
      {pairedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {`Runs paired ${formatDateTime(pairedAt)}. Rechecked every two minutes.`}
        </Typography>
      )}

      {journeys !== null && !corridors.length && (
        <Alert severity="info">
          No runs between areas yet. A run is a vehicle leaving one geofenced area and reaching
          another, so this fills once two areas have been drawn under Configuration and a tracked
          vehicle has travelled between them.
        </Alert>
      )}

      {!!corridors.length && (
        <>
          <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Run"
              value={chosen}
              onChange={(e) => setCorridor(e.target.value)}
              sx={{ minWidth: 320 }}
            >
              {corridors.map((c) => (
                <MenuItem key={c.key} value={c.key}>
                  {`${c.from} → ${c.to} · ${c.runs} trip${c.runs === 1 ? '' : 's'}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }} value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }} value={to} onChange={(e) => setTo(e.target.value)} />
            <TextField
              select
              size="small"
              label="Vehicle"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              sx={{ minWidth: 190 }}
            >
              <MenuItem value="">{`All vehicles (${lorries.length})`}</MenuItem>
              {lorries.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch size="small" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} />}
              label="Late only"
            />
            <Box sx={{ flex: 1 }} />
            <DownloadTable name="area-journeys" title="Area journeys" rows={runs} columns={columns} />
          </Stack>

          {/* Which of the filters is doing the narrowing, and one place to undo it. Without this a
              page showing nine runs out of two hundred looks like a page with nine runs. */}
          {(vehicleId !== '' || lateOnly || when) && (
            <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {`Showing ${runs.length} of ${onLeg.length} trips on this leg`}
              </Typography>
              {vehicleId !== '' && <Chip size="small" label={lorries.find((v) => v.id === Number(vehicleId))?.name} onDelete={() => setVehicleId('')} />}
              {lateOnly && <Chip size="small" label="Late only" onDelete={() => setLateOnly(false)} />}
              {when && <Chip size="small" label={`${DAYS[when.day]} ${String(when.hour).padStart(2, '0')}:00`} onDelete={() => setWhen(null)} />}
            </Stack>
          )}

          {!runs.length && (
            <Alert severity="info">
              {onLeg.length
                ? 'No trips match those filters on this leg. Clear one above to widen it.'
                : 'No trips on that leg in these dates.'}
            </Alert>
          )}

          {!!head && (
            <>
              <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                <Stack direction="row" gap={3} flexWrap="wrap" alignItems="baseline">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Usually takes</Typography>
                    <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{humanMs(head.usual)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Trips counted</Typography>
                    <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>{head.runs}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Quickest to slowest</Typography>
                    <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {`${humanMs(head.quickest)} – ${humanMs(head.slowest)}`}
                    </Typography>
                  </Box>
                  {head.lateRuns > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Ran over</Typography>
                      <Typography variant="h6" color="error.main" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {`${head.lateRuns} of ${head.runs}`}
                      </Typography>
                    </Box>
                  )}
                </Stack>
                {!head.trustworthy && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    {`Only ${head.runs} run${head.runs === 1 ? '' : 's'} on this leg so far — too few to say what is normal.`}
                  </Typography>
                )}
                {worstCell && (
                  <Typography variant="body2" color="error.main" fontWeight={600} sx={{ mt: 1 }}>
                    {`Slowest around ${DAYS[worstCell.day]} ${String(worstCell.hour).padStart(2, '0')}:00 — ${humanMs(worstCell.ms)} across ${worstCell.runs} trips.`}
                  </Typography>
                )}
              </Paper>

              {/* Which lorry did how many. Above the heatmap because "who" is the question people
                  arrive with, and clicking a row is how they then narrow everything below it. */}
              {perVehicle.length > 1 && (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>By vehicle</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Trips each truck turned on this leg, and how many went over the usual time.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Vehicle', 'Driver', 'Trips', 'Usually', 'Over the usual', 'Worst'].map((h) => (
                            <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {perVehicle.map((v) => (
                          <TableRow
                            key={v.id}
                            hover
                            selected={Number(vehicleId) === v.id}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setVehicleId(Number(vehicleId) === v.id ? '' : v.id)}
                          >
                            <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{v.name}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.driver || '—'}</TableCell>
                            <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{v.runs}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{humanMs(v.usual)}</TableCell>
                            {/* Out of how many, because three late out of four is a different story
                                from three out of ninety. */}
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.late ? `${v.late} of ${v.runs}` : '—'}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.worstMs > 0 ? `+${humanMs(v.worstMs)}` : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* When it goes wrong, which is usually a time of day rather than a lorry. */}
              {!!cells.length && (
                <Paper variant="outlined" sx={{ p: 2.5, mb: 2, overflowX: 'auto' }}>
                  <Typography variant="subtitle1" fontWeight={700}>When it is slow</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Each square is the usual time for trips that set off in that hour. Darker is slower.
                  </Typography>
                  <Box sx={{ display: 'inline-block', minWidth: 'min-content' }}>
                    <Stack direction="row" gap={0.5} sx={{ ml: '38px', mb: 0.5 }}>
                      {hours.map((h) => (
                        <Box key={h} sx={{ width: 26, textAlign: 'center' }}>
                          <Typography variant="caption" color="text.secondary">{String(h).padStart(2, '0')}</Typography>
                        </Box>
                      ))}
                    </Stack>
                    {days.map((index) => DAYS[index]).map((name, position) => {
                      const index = days[position];
                      return (
                        <Stack key={name} direction="row" gap={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ width: 34, textAlign: 'right', mr: 0.5 }}>{name}</Typography>
                          {hours.map((h) => {
                            const cell = cells.find((c) => c.day === index && c.hour === h);
                            if (!cell) return <Box key={h} sx={{ width: 26, height: 26, borderRadius: 0.5, bgcolor: 'action.hover' }} />;
                            // Shaded against the slowest run on this leg, so the scale means something
                            // on a corridor of twenty minutes and one of nine hours alike.
                            const depth = slowest > 0 ? Math.max(0.15, cell.ms / slowest) : 0.15;
                            return (
                              <Tooltip
                                key={h}
                                title={`${name} ${String(h).padStart(2, '0')}:00 · ${humanMs(cell.ms)} · ${cell.runs} trip${cell.runs === 1 ? '' : 's'}${cell.runs < 2 ? ' (too few to rely on)' : ''} — click to list them`}
                              >
                                <Box
                                  onClick={() => setWhen(
                                    when && when.day === index && when.hour === h ? null : { day: index, hour: h },
                                  )}
                                  sx={{
                                    cursor: 'pointer',
                                    outline: when && when.day === index && when.hour === h ? '2px solid' : 'none',
                                    outlineColor: 'text.primary',
                                    width: 26,
                                    height: 26,
                                    borderRadius: 0.5,
                                    bgcolor: 'error.main',
                                    opacity: depth,
                                    // One run is an anecdote. Outlined rather than filled, so a dark
                                    // square always means several runs agreed.
                                    border: cell.runs < 2 ? '1px dashed' : 'none',
                                    borderColor: 'error.main',
                                  }}
                                />
                              </Tooltip>
                            );
                          })}
                        </Stack>
                      );
                    })}
                  </Box>
                </Paper>
              )}

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vehicle</TableCell>
                      <TableCell>Left</TableCell>
                      <TableCell>Arrived</TableCell>
                      <TableCell>Took</TableCell>
                      <TableCell>Against usual</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {runs.slice(0, 100).map((run) => {
                      const over = typeof run.overMs === 'number' ? run.overMs : null;
                      return (
                        <TableRow
                          key={`${run.vehicleId}-${run.leftAt}`}
                          hover
                          sx={{ cursor: run.tripId ? 'pointer' : 'default' }}
                          onClick={() => run.tripId && navigate(`/trips/${run.tripId}`)}
                        >
                          <TableCell>{data.vehicles?.find((v) => v.id === run.vehicleId)?.name || `Vehicle ${run.vehicleId}`}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(run.leftAt)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(run.arrivedAt)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{humanMs(run.ms)}</TableCell>
                          <TableCell>
                            {over == null
                              ? <Chip size="small" variant="outlined" label="No usual yet" />
                              : over > 0
                                ? <Chip size="small" color="error" label={`${humanMs(over)} over`} />
                                : <Chip size="small" color="success" variant="outlined" label={`${humanMs(-over)} under`} />}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Open a run to see where its time went, stage by stage.
              </Typography>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default BetweenAreasPage;
